import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { AgentPlatformTableActionsCell } from '../../../components/AgentPlatform/AgentPlatformTableActionsCell';
import { StatusBadge } from '../../../components/AgentPlatform/StatusBadge';
import { Button } from '../../../components/UI/Button';
import { ConfirmModal } from '../../../components/UI/ConfirmModal';
import { useToast } from '../../../components/UI/ToastProvider';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type { AgentKnowledgeBaseDto } from '../../../types/agentPlatform';
import { labelAgentPlatformScope } from '../../../utils/agentPlatformLabels';
import { apTheme } from '../../../utils/agentPlatformTheme';

export const AgentKnowledgeBasesPage: React.FC = () => {
  const [items, setItems] = useState<AgentKnowledgeBaseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [archiveTarget, setArchiveTarget] = useState<AgentKnowledgeBaseDto | null>(null);
  const { addToast } = useToast();

  const load = () => {
    setLoading(true);
    agentPlatformService
      .getKnowledgeBases()
      .then(setItems)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await agentPlatformService.archiveKnowledgeBase(archiveTarget.id);
      addToast('Base arquivada.', 'success');
      setArchiveTarget(null);
      load();
    } catch {
      addToast('Não foi possível arquivar.', 'error');
    }
  };

  return (
    <AgentPlatformFeatureGate feature="agents">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
          <AgentPlatformPageHeader
            title="Base de Conhecimento"
            subtitle="Catálogo de bases de conhecimento vinculáveis aos agentes para RAG."
            area="knowledge"
            actions={
              <Link to="/agent-platform/knowledge-bases/new">
                <Button>Nova base</Button>
              </Link>
            }
          />

          <div className={apTheme.tableWrap}>
            <table className="min-w-full divide-y divide-slate-100">
              <thead>
                <tr className={apTheme.tableHead}>
                  <th className="px-4 py-3 text-left">Nome</th>
                  <th className="px-4 py-3 text-left">Código</th>
                  <th className="px-4 py-3 text-left">Escopo</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className={apTheme.tableActionsHead}>Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      Carregando…
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map(kb => (
                    <tr key={kb.id} className="hover:bg-red-50/40 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{kb.name}</td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-600">{kb.code}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{labelAgentPlatformScope(kb.scope)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={kb.isActive}
                          label={kb.isActive ? 'Ativa' : 'Inativa'}
                        />
                      </td>
                      <AgentPlatformTableActionsCell>
                        <Link to={`/agent-platform/knowledge-bases/${kb.id}/edit`} className="inline-flex">
                          <Button variant="secondary" size="sm" type="button">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </Button>
                        </Link>
                        <Button
                          variant="danger"
                          size="sm"
                          type="button"
                          onClick={() => setArchiveTarget(kb)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </Button>
                      </AgentPlatformTableActionsCell>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <ConfirmModal
            isOpen={!!archiveTarget}
            title="Arquivar base"
            message={`Arquivar "${archiveTarget?.name}"? Agentes que usam esta base podem deixar de funcionar.`}
            onConfirm={handleArchive}
            onCancel={() => setArchiveTarget(null)}
          />
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
